"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.encode = exports.decode = void 0;
// From https://github.com/aws/amazon-ssm-agent/blob/7ca3c5bc019da3801023c54ef3514d5cdceac9f0/agent/session/contracts/agentmessage.go
var js_sha256_1 = require("js-sha256");
var uuid_1 = require("uuid");
// HL - HeaderLength is a 4 byte integer that represents the header length.
// MessageType is a 32 byte UTF-8 string containing the message type.
// SchemaVersion is a 4 byte integer containing the message schema version number.
// CreatedDate is an 8 byte integer containing the message create epoch millis in UTC.
// SequenceNumber is an 8 byte integer containing the message sequence number for serialized message streams.
// Flags is an 8 byte unsigned integer containing a packed array of control flags:
//   Bit 0 is SYN - SYN is set (1) when the recipient should consider Seq to be the first message number in the stream
//   Bit 1 is FIN - FIN is set (1) when this message is the final message in the sequence.
// MessageId is a 40 byte UTF-8 string containing a random UUID identifying this message.
// Payload digest is a 32 byte containing the SHA-256 hash of the payload.
// Payload Type is a 4 byte integer containing the payload type.
// Payload length is an 4 byte unsigned integer containing the byte length of data in the Payload field.
// Payload is a variable length byte data.
//
// | HL|         MessageType           |Ver|  CD   |  Seq  | Flags |
// |         MessageId                     |           Digest              |PayType| PayLen|
// |         Payload      			|
var Length;
(function (Length) {
    Length[Length["HL"] = 4] = "HL";
    Length[Length["MessageType"] = 32] = "MessageType";
    Length[Length["SchemaVersion"] = 4] = "SchemaVersion";
    Length[Length["CreatedDate"] = 8] = "CreatedDate";
    Length[Length["SequenceNumber"] = 8] = "SequenceNumber";
    Length[Length["Flags"] = 8] = "Flags";
    Length[Length["MessageId"] = 16] = "MessageId";
    Length[Length["PayloadDigest"] = 32] = "PayloadDigest";
    Length[Length["PayloadType"] = 4] = "PayloadType";
    Length[Length["PayloadLength"] = 4] = "PayloadLength";
})(Length || (Length = {}));
var Offset;
(function (Offset) {
    Offset[Offset["HL"] = 0] = "HL";
    Offset[Offset["MessageType"] = 4] = "MessageType";
    Offset[Offset["SchemaVersion"] = 36] = "SchemaVersion";
    Offset[Offset["CreatedDate"] = 40] = "CreatedDate";
    Offset[Offset["SequenceNumber"] = 48] = "SequenceNumber";
    Offset[Offset["Flags"] = 56] = "Flags";
    Offset[Offset["MessageId"] = 64] = "MessageId";
    Offset[Offset["PayloadDigest"] = 80] = "PayloadDigest";
    Offset[Offset["PayloadType"] = 112] = "PayloadType";
    Offset[Offset["PayloadLength"] = 116] = "PayloadLength";
    Offset[Offset["Payload"] = 120] = "Payload";
})(Offset || (Offset = {}));
/**
 * Decode a UTF-8 byte sequence to a string.
 */
function decodeText(buffer, start, end) {
    var decoder = new TextDecoder('utf8');
    var decoded = decoder.decode(buffer.slice(start, end));
    // Strip errant NULs.
    return decoded.replace(/\0/g, '');
}
function putString(buffer, start, end, value) {
    var len = end - start;
    if (len < value.length) {
        throw new Error('String is too long to encode');
    }
    for (var i = 0; i < len; i++) {
        var byte = i >= value.length ? 32 : value.charCodeAt(i);
        buffer.set([byte], start + i);
    }
}
/**
 * Decode a byte sequence to an integer.
 */
function decodeInt(buffer, start, end) {
    var bytes = buffer.slice(start, end);
    return bytes.reduce(function (acc, byte) {
        return (acc << 8) + byte;
    }, 0);
}
/**
 * Encode a (32-bit) integer into a byte sequence.
 */
function putInt(buffer, pos, value) {
    var bytes = [
        (value >> 24) & 0xff,
        (value >> 16) & 0xff,
        (value >> 8) & 0xff,
        value & 0xff,
    ];
    // console.log( value, bytes );
    buffer.set(bytes, pos);
}
/**
 * Encode a (64-bit) long into a byte sequence.
 *
 * We cheat here; JS doesn't support 64-bit integers (aka longs), so we can
 * just use putInt with a 4 byte offset.
 */
function putLong(buffer, pos, value) {
    putInt(buffer, pos + 4, value);
}
/**
 * Decode a UUID field to a string.
 *
 * putUuid splits this into most-significant and least-significant longs, then
 * packs them, so we need to flip back.
 */
function decodeUuid(buffer, start, end) {
    var bytes = buffer.subarray(start, end);
    var leastSignificant = bytes.subarray(0, 8);
    var mostSignificant = bytes.subarray(8, 16);
    var raw = longToHex(mostSignificant) + longToHex(leastSignificant);
    // Reformat for display.
    //  8-4-4-4-12
    return [
        raw.substr(0, 8),
        raw.substr(8, 4),
        raw.substr(12, 4),
        raw.substr(16, 4),
        raw.substr(20),
    ].join('-');
}
/**
 * Convert a long to hexadecimal string.
 */
function longToHex(buffer) {
    return buffer.reduce(function (str, byte) { return str + byte.toString(16).padStart(2, '0'); }, '');
}
/**
 * Decode a message.
 */
function decode(buffer) {
    var headerLength = decodeInt(buffer, Offset.HL, Offset.HL + Length.HL);
    return {
        MessageType: decodeText(buffer, Offset.MessageType, Offset.MessageType + Length.MessageType).trimEnd(),
        SequenceNumber: decodeInt(buffer, Offset.SequenceNumber, Offset.SequenceNumber + Length.SequenceNumber),
        MessageId: decodeUuid(buffer, Offset.MessageId, Offset.MessageId + Length.MessageId),
        PayloadType: decodeInt(buffer, Offset.PayloadType, Offset.PayloadType + Length.PayloadType),
        Payload: decodeText(buffer, headerLength + Length.PayloadLength),
    };
}
exports.decode = decode;
/**
 * Encode a message.
 */
function encode(message) {
    var payloadLength = message.Payload.length;
    var totalLength = Offset.PayloadLength + payloadLength + 4;
    // | HL|         MessageType           |Ver|  CD   |  Seq  | Flags |
    // |         MessageId                     |           Digest              |PayType| PayLen|
    // |         Payload      			|
    var data = new Uint8Array(totalLength);
    putInt(data, Offset.HL, Offset.PayloadLength);
    putString(data, Offset.MessageType, Offset.MessageType + Length.MessageType, message.MessageType);
    putInt(data, Offset.SchemaVersion, 1);
    putLong(data, Offset.CreatedDate, Date.now());
    putLong(data, Offset.SequenceNumber, message.SequenceNumber);
    putLong(data, Offset.Flags, message.SequenceNumber > 0 ? 0 : 1);
    // Generate a UUID.
    uuid_1.v4({}, data, Offset.MessageId);
    // Generate the digest.
    data.set(js_sha256_1.sha256.update(message.Payload).digest(), Offset.PayloadDigest);
    // Set the payload.
    putInt(data, Offset.PayloadType, message.PayloadType);
    putInt(data, Offset.PayloadLength, payloadLength);
    putString(data, Offset.Payload, totalLength, message.Payload);
    return data;
}
exports.encode = encode;
